import { BreakPointCondition, useBreakpointIf, Stack, Icon } from '@genial/components';
import { Color, Typography } from '@genial/design-tokens';
import { useNavigation } from '@react-navigation/native';
import { Box, Button } from 'native-base';
import { useCallback, useEffect, useMemo } from 'react';
import { useRecoilState } from 'recoil';

import { OrderByDropdown } from '../../../../../../components/OrderByDropdown';
import { OrderByModal, OrderByModalTypes } from '../../../../../../components/OrderByModal';
import { SignedInNavigationScreenNavigationProp } from '../../../../../../routes/types';
import { assetsOrderByAtom } from '../../../../../../store/product/filters';
import { useGlobalAssetContext } from '../../contexts';
import { useGlobalAssetController } from '../../hooks';

import { getOrderValuesByValue } from './constants';

export const AssetOrderBy = () => {
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();

  const [{ appliedOrderBy }, setAssetsFiltersAtom] = useRecoilState(assetsOrderByAtom);

  const { hasSomeLoading, selectedTab, orderBy, isDesktop } = useGlobalAssetContext();
  const { handleOrderBy } = useGlobalAssetController();

  const orderByOptions: OrderByModalTypes.OrderByStructure[] = useMemo(() => {
    const headerItems = selectedTab.tableHead;

    return headerItems
      .filter(({ orderValue }) => !!orderValue)
      .map(({ orderValue, title }) => {
        return {
          ...getOrderValuesByValue(orderValue as string),
          label: title,
          value: orderValue as string,
        } as OrderByModalTypes.OrderByStructure;
      });
  }, [selectedTab.tableHead]);

  const getIsOrdering = () => {
    if (orderBy) {
      const currentOrderBy = selectedTab.tableHead.find(
        ({ orderValue }) => orderValue === orderBy.value
      );

      return !!currentOrderBy?.title;
    }

    return false;
  };

  useEffect(() => {
    setAssetsFiltersAtom((prev) => ({
      ...prev,
      availableOrderBy: orderByOptions,
      appliedOrderBy: undefined,
    }));
  }, [orderByOptions, setAssetsFiltersAtom]);

  useEffect(() => {
    handleOrderBy(appliedOrderBy?.value || '', appliedOrderBy?.direction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedOrderBy]);

  if (!orderByOptions.length) {
    return <Stack />;
  }

  if (isDesktop && !selectedTab.hideTableHead) {
    return <></>;
  }

  const isOrdering = getIsOrdering();

  if (isDesktop) {
    return <OrderByDropdown orderByAtom={assetsOrderByAtom} />;
  }

  return (
    <Button
      onPress={() => navigate('BatchTicketAssetOrderByModal')}
      isDisabled={hasSomeLoading}
      alignItems="center"
      justifyContent="center"
      variant="secondary"
      bg="white"
      w="52px">
      <Box>
        <Icon name="Ordering" size="xxs" />
        {isOrdering && (
          <Box
            w={Typography['fontsize.md']}
            h={Typography['fontsize.md']}
            borderRadius="full"
            background={Color['brand.tertiary.light']}
            position="absolute"
            top={-12}
            right={-22}
          />
        )}
      </Box>
    </Button>
  );
};

export const BatchTicketAssetOrderByModal = () => {
  const navigation = useNavigation();
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');

  const goBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  const getSliderModalWidth = () => {
    if (isMobile) {
      return 1;
    }
    if (isTablet) {
      return 0.5;
    }
    return 0.3;
  };

  return (
    <OrderByModal
      onClose={goBack}
      sliderModalWidth={getSliderModalWidth()}
      orderByAtom={assetsOrderByAtom}
    />
  );
};
